/**
 * v1.27.95 — Auto-Festival calendar for MICM.
 *
 * Each festival is active from (date − 4 days) through (date + 1 day) inclusive,
 * so 6 calendar days total. `getActiveFestival(today)` picks the closest one.
 *
 * Dates for movable Hindu / Muslim festivals are hard-coded per year because
 * they follow lunar calendars — refresh when a new year is added.
 *
 * A URL query param `?festival=<key>` (e.g. `?festival=diwali`) lets the admin
 * preview any theme without changing the system date. Handled in FestivalBanner.
 */

// Palette per festival — gradient (from/via/to), accent, and emoji marker.
// Keep colours warm and subtle so they don't overwhelm data density.
export const FESTIVALS = {
    diwali: {
        key: "diwali", name: "Diwali", greeting: "Happy Diwali",
        from: "#f59e0b", via: "#b91c1c", to: "#7c2d12",
        text: "text-amber-50", accent: "text-amber-200", emoji: "🪔",
        tagline: "Wishing you light, prosperity and joy",
    },
    holi: {
        key: "holi", name: "Holi", greeting: "Happy Holi",
        from: "#ec4899", via: "#8b5cf6", to: "#2563eb",
        text: "text-white", accent: "text-yellow-100", emoji: "🎨",
        tagline: "May your days be filled with vibrant colours",
    },
    dussehra: {
        key: "dussehra", name: "Dussehra", greeting: "Happy Dussehra",
        from: "#dc2626", via: "#ea580c", to: "#facc15",
        text: "text-white", accent: "text-yellow-100", emoji: "🏹",
        tagline: "Victory of good over evil",
    },
    eid_ul_fitr: {
        key: "eid_ul_fitr", name: "Eid ul Fitr", greeting: "Eid Mubarak",
        from: "#059669", via: "#14b8a6", to: "#0d9488",
        text: "text-white", accent: "text-emerald-100", emoji: "🌙",
        tagline: "May peace and prosperity be with you",
    },
    eid_ul_adha: {
        key: "eid_ul_adha", name: "Eid ul Adha", greeting: "Eid Mubarak",
        from: "#0f766e", via: "#0d9488", to: "#065f46",
        text: "text-white", accent: "text-emerald-100", emoji: "🕌",
        tagline: "Wishing you a blessed Eid",
    },
    christmas: {
        key: "christmas", name: "Christmas", greeting: "Merry Christmas",
        from: "#7f1d1d", via: "#166534", to: "#065f46",
        text: "text-white", accent: "text-red-100", emoji: "🎄",
        tagline: "Warm wishes for a joyful season",
    },
    new_year: {
        key: "new_year", name: "New Year", greeting: "Happy New Year",
        from: "#312e81", via: "#7c3aed", to: "#c026d3",
        text: "text-white", accent: "text-yellow-100", emoji: "🎆",
        tagline: "May the new year bring success and happiness",
    },
    republic_day: {
        key: "republic_day", name: "Republic Day", greeting: "Happy Republic Day",
        from: "#FF9933", via: "#ffffff", to: "#138808",
        text: "text-slate-900", accent: "text-blue-800", emoji: "🇮🇳",
        tagline: "Saluting the Constitution and the Republic",
    },
    independence_day: {
        key: "independence_day", name: "Independence Day", greeting: "Happy Independence Day",
        from: "#FF9933", via: "#ffffff", to: "#138808",
        text: "text-slate-900", accent: "text-blue-800", emoji: "🇮🇳",
        tagline: "Jai Hind — celebrating our freedom",
    },
    ganesh_chaturthi: {
        key: "ganesh_chaturthi", name: "Ganesh Chaturthi", greeting: "Ganpati Bappa Morya",
        from: "#f97316", via: "#dc2626", to: "#9f1239",
        text: "text-white", accent: "text-yellow-100", emoji: "🐘",
        tagline: "May Bappa remove every obstacle from your path",
    },
    navratri: {
        key: "navratri", name: "Navratri", greeting: "Happy Navratri",
        from: "#eab308", via: "#f43f5e", to: "#7c3aed",
        text: "text-white", accent: "text-yellow-100", emoji: "🌺",
        tagline: "Nine nights of devotion, dance and light",
    },
};

// Movable + fixed dates. Add new years by appending to the array.
// Format: [YYYY-MM-DD, festival_key]
export const FESTIVAL_CALENDAR = [
    // 2026
    ["2026-01-01", "new_year"],
    ["2026-01-26", "republic_day"],
    ["2026-03-04", "holi"],
    ["2026-03-21", "eid_ul_fitr"],
    ["2026-05-28", "eid_ul_adha"],
    ["2026-08-15", "independence_day"],
    ["2026-09-14", "ganesh_chaturthi"],
    ["2026-10-11", "navratri"],
    ["2026-10-19", "dussehra"],
    ["2026-11-08", "diwali"],
    ["2026-12-25", "christmas"],
    // 2027
    ["2027-01-01", "new_year"],
    ["2027-01-26", "republic_day"],
    ["2027-03-22", "holi"],
    ["2027-03-10", "eid_ul_fitr"],
    ["2027-05-17", "eid_ul_adha"],
    ["2027-08-15", "independence_day"],
    ["2027-09-04", "ganesh_chaturthi"],
    ["2027-09-30", "navratri"],
    ["2027-10-08", "dussehra"],
    ["2027-10-29", "diwali"],
    ["2027-12-25", "christmas"],
    // 2028 head-start
    ["2028-01-01", "new_year"],
    ["2028-01-26", "republic_day"],
];

const DAY_MS = 24 * 60 * 60 * 1000;

function _asDate(v) {
    if (v instanceof Date) return v;
    if (typeof v === "string") {
        const [y, m, d] = v.split("-").map(Number);
        return new Date(y, m - 1, d);
    }
    return new Date();
}

/**
 * Returns the active festival descriptor for `today`, or null when none is
 * within its [date − 4, date + 1] window. Ties broken by the CLOSEST festival
 * date (so a festival on the exact day beats one 4 days out).
 */
export function getActiveFestival(today = new Date()) {
    const t = _asDate(today);
    // Zero out time so comparisons are day-accurate.
    t.setHours(0, 0, 0, 0);
    let best = null;
    let bestGap = Infinity;
    for (const [iso, key] of FESTIVAL_CALENDAR) {
        const fd = _asDate(iso);
        const diff = Math.round((t - fd) / DAY_MS); // negative = before, positive = after
        // Window: 4 days before through 1 day after.
        if (diff < -4 || diff > 1) continue;
        const gap = Math.abs(diff);
        if (gap < bestGap) {
            bestGap = gap;
            best = { ...FESTIVALS[key], date: iso, diff };
        }
    }
    return best;
}

/**
 * Preview override — reads `?festival=<key>` from the URL. Returns the
 * matching festival descriptor with a synthetic diff of 0, or null.
 */
export function getPreviewFestival(search = window.location.search) {
    try {
        const p = new URLSearchParams(search);
        const key = (p.get("festival") || "").toLowerCase().trim();
        if (!key) return null;
        const fest = FESTIVALS[key];
        if (!fest) return null;
        return { ...fest, date: "PREVIEW", diff: 0, _preview: true };
    } catch {
        return null;
    }
}
